import { useEffect, useState } from "react";
import { SITE_CONFIG } from "@/config/site";
import { track } from "@/lib/analytics";

const links = [
  { label: "Desejo", href: "#desejo" },
  { label: "Transformação", href: "#transformacao" },
  { label: "Produto", href: "#produto" },
  { label: "Ecossistema", href: "#recursos" },
  { label: "Para quem", href: "#para-quem" },
  { label: "Planos", href: "#planos" },
  { label: "Dúvidas", href: "#faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-[70] transition-all duration-500 ${
        scrolled || open
          ? "border-b border-foreground-800/60 bg-foreground-950/90 backdrop-blur-xl"
          : "bg-transparent"
      }`}
      style={
        scrolled || open
          ? { backgroundColor: "oklch(var(--foreground-950) / 0.9)" }
          : undefined
      }
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:h-20 md:px-6">
        {/* Logo */}
        <a
          href="#top"
          className="flex items-center gap-2.5"
          aria-label="PlayMenu — voltar ao topo"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500">
            <i className="ri-play-fill text-lg leading-none text-foreground-950" aria-hidden="true" />
          </span>
          <span className="font-heading text-xl font-bold tracking-tight text-foreground-50">
            Play<span className="text-primary-500">Menu</span>
          </span>
        </a>

        {/* Links desktop */}
        <div className="hidden items-center gap-1 lg:flex">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground-200 transition-colors hover:bg-foreground-800/60 hover:text-foreground-50"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={SITE_CONFIG.instagramUrl}
            target="_blank"
            rel="nofollow noopener noreferrer"
            aria-label="Instagram da PlayMenu"
            className="hidden h-9 w-9 items-center justify-center rounded-full text-foreground-200 transition-colors hover:bg-foreground-800/60 hover:text-foreground-50 sm:flex"
            onClick={() => track("instagram_click", { location: "header" })}
          >
            <i className="ri-instagram-line text-lg" aria-hidden="true" />
          </a>

          <a
            href="https://playmenu.app/cadastro?tipo=restaurante"
            target="_blank"
            rel="noopener"
            className="hidden whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400 md:inline-flex"
            onClick={() => track("hero_cta_click", { location: "header" })}
          >
            Experimentar grátis
          </a>

          {/* Hamburger mobile */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            aria-expanded={open}
            className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-50 transition-colors hover:bg-foreground-800/60 lg:hidden"
          >
            <i
              className={`text-xl transition-transform duration-300 ${
                open ? "ri-close-line rotate-90" : "ri-menu-line"
              }`}
              aria-hidden="true"
            />
          </button>
        </div>
      </nav>

      {/* Menu mobile */}
      <div
        className={`fixed inset-x-0 top-16 bottom-0 z-[60] origin-top transition-all duration-300 md:top-20 lg:hidden ${
          open ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        {/* Camada de fundo translúcida — garante a leitura do texto */}
        <div
          className="absolute inset-0 backdrop-blur-xl"
          style={{ backgroundColor: "oklch(var(--foreground-950) / 0.96)" }}
          aria-hidden="true"
        />
        <div className="relative flex h-full flex-col justify-between px-6 py-8">
          <nav className="flex flex-col gap-1">
            {links.map((link, i) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between border-b border-foreground-800/60 py-4 font-heading text-2xl font-semibold text-foreground-50 transition-colors hover:text-primary-500"
                style={{ transitionDelay: `${i * 30}ms` }}
              >
                {link.label}
                <i className="ri-arrow-right-up-line text-lg text-foreground-500" aria-hidden="true" />
              </a>
            ))}
          </nav>

          <div className="flex flex-col gap-3 pb-6">
            <a
              href="https://playmenu.app/cadastro?tipo=restaurante"
              target="_blank"
              rel="noopener"
              onClick={() => setOpen(false)}
              className="flex w-full items-center justify-center rounded-full bg-primary-500 px-6 py-4 text-base font-semibold text-foreground-950 transition-colors hover:bg-primary-400"
            >
              Experimentar grátis
            </a>
            <a
              href={SITE_CONFIG.instagramUrl}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-full border border-foreground-700 px-6 py-4 text-base font-semibold text-foreground-50 transition-colors hover:bg-foreground-800/60"
            >
              <i className="ri-instagram-line" aria-hidden="true" />
              {SITE_CONFIG.instagramHandle}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}